import { useEffect, useState, useCallback } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FloppyDisk, Envelope, ArrowsClockwise, ChartLineUp } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import RichTextEditor from "@/pages/marketing/RichTextEditor";

// The 4 admin-editable knobs for the "Send Inquiry Email" button on the
// Booking Check-ins page. Stored in `system_config` and rendered via
// the backend's `_render_inquiry_template` (booking_checks.py).
//
// Body field uses the SHARED marketing RichTextEditor so the operator
// gets the exact same authoring experience as the campaign step
// editor: Designer / HTML toggle, font dropdown, color, snippets that
// drop as `{{SNIPPET:cblk_…}}` tokens (server-side expanded), assets,
// variable picker, etc. Single source of truth — no bespoke editor
// drift between this tab and the rest of the platform.
const KEYS = [
  { key: "booking_inquiry_subject",    label: "Subject line",       type: "input" },
  { key: "booking_inquiry_from_name",  label: "From name",          type: "input" },
  { key: "booking_inquiry_from_email", label: "From email address", type: "input" },
  { key: "booking_inquiry_body",       label: "Body — full WYSIWYG editor (same toolbar as campaign steps)", type: "richtext" },
];

const PLACEHOLDER_HELP = (
  <span>
    Supported placeholders:{" "}
    <code className="bg-[#F5F5F5] px-1 py-0.5 text-[10px]">{"{{FirstName}}"}</code>,{" "}
    <code className="bg-[#F5F5F5] px-1 py-0.5 text-[10px]">{"{{LastName}}"}</code>,{" "}
    <code className="bg-[#F5F5F5] px-1 py-0.5 text-[10px]">{"{{FullName}}"}</code>,{" "}
    <code className="bg-[#F5F5F5] px-1 py-0.5 text-[10px]">{"{{Company}}"}</code>{" "}
    — pick them from the <strong>Variable</strong> dropdown in the editor toolbar.
  </span>
);

export default function BookingInquiryTemplateTab() {
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [metrics, setMetrics] = useState(null);
  const [metricsLoading, setMetricsLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const results = await Promise.all(
        KEYS.map(k => api.get(`/admin/config/${k.key}`).then(r => [k.key, r.data?.config_value || ""]))
      );
      const next = {};
      results.forEach(([k, v]) => { next[k] = v; });
      setValues(next);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load template");
    }
    setLoading(false);
  }, []);

  const loadMetrics = useCallback(async () => {
    setMetricsLoading(true);
    try {
      const r = await api.get("/admin/booking-inquiry-template/metrics");
      setMetrics(r.data);
    } catch {
      // Quiet failure — keeps the rest of the tab usable.
      setMetrics(null);
    }
    setMetricsLoading(false);
  }, []);

  useEffect(() => { load(); loadMetrics(); }, [load, loadMetrics]);

  const update = (k, v) => setValues(prev => ({ ...prev, [k]: v }));

  const saveAll = async () => {
    setSaving(true);
    try {
      for (const k of KEYS) {
        await api.put(`/admin/config/${k.key}`, { config_value: values[k.key] ?? "" });
      }
      toast.success("Booking inquiry template saved.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <div className="p-8 flex justify-center" data-testid="inquiry-template-loading">
        <div className="w-8 h-8 border-2 border-[#0055FF] border-t-transparent animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="booking-inquiry-template-tab">
      {/* Metrics card — added Session 82 follow-up so Mike can see
          at a glance whether the curated copy is converting. Reply
          detection: any inbound email_messages row tied to the same
          lead landing within 7 days of the inquiry send. */}
      <Card className="border border-[#E5E5E5] rounded-none p-5" data-testid="inquiry-metrics-card">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
          <div>
            <h2 className="text-xs font-bold uppercase tracking-[0.15em] text-[#A3A3A3] flex items-center gap-1.5">
              <ChartLineUp size={14} /> Inquiry reply rate
            </h2>
            <p className="text-xs text-[#525252] mt-1 max-w-2xl leading-snug">
              A "reply" is any inbound email from the lead landing within 7 days of
              the inquiry send. Use the rolling 30-day numbers to A/B-test new copy
              against the all-time baseline.
            </p>
          </div>
          <button
            onClick={loadMetrics}
            disabled={metricsLoading}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 border border-[#E5E5E5] text-[#525252] text-[10px] font-bold uppercase tracking-wider hover:border-[#0055FF] hover:text-[#0055FF] disabled:opacity-50"
            data-testid="inquiry-metrics-refresh"
          >
            <ArrowsClockwise size={11} weight="bold" />
            {metricsLoading ? "Refreshing…" : "Refresh"}
          </button>
        </div>

        {!metrics && !metricsLoading && (
          <p className="text-xs text-[#A3A3A3]" data-testid="inquiry-metrics-empty">
            No metrics available yet — fire the inquiry on at least one lead to populate the dashboard.
          </p>
        )}

        {metrics && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="border border-[#F5F5F5] p-3" data-testid="metric-total-sent">
              <div className="text-[10px] font-bold uppercase tracking-wider text-[#A3A3A3]">Total sent (all-time)</div>
              <div className="text-2xl font-black tracking-tight text-[#0A0A0A] mt-1">{metrics.total_sent}</div>
              <div className="text-[10px] text-[#525252] mt-1">{metrics.total_replied_7d} replied within 7 days</div>
            </div>
            <div className="border border-[#F5F5F5] p-3" data-testid="metric-reply-rate">
              <div className="text-[10px] font-bold uppercase tracking-wider text-[#A3A3A3]">All-time reply rate</div>
              <div className="text-2xl font-black tracking-tight text-[#0A0A0A] mt-1">{metrics.reply_rate_pct}%</div>
              <div className="text-[10px] text-[#525252] mt-1">Industry benchmark: 5–10%</div>
            </div>
            <div className="border border-[#F5F5F5] p-3" data-testid="metric-30d-sent">
              <div className="text-[10px] font-bold uppercase tracking-wider text-[#A3A3A3]">Last 30 days</div>
              <div className="text-2xl font-black tracking-tight text-[#0A0A0A] mt-1">{metrics.last_30d_sent}</div>
              <div className="text-[10px] text-[#525252] mt-1">{metrics.last_30d_replied_7d} replied · {metrics.last_30d_reply_rate_pct}%</div>
            </div>
            <div className="border border-[#F5F5F5] p-3" data-testid="metric-median-hours">
              <div className="text-[10px] font-bold uppercase tracking-wider text-[#A3A3A3]">Median reply time</div>
              <div className="text-2xl font-black tracking-tight text-[#0A0A0A] mt-1">
                {metrics.median_reply_hours == null ? "—" : `${metrics.median_reply_hours}h`}
              </div>
              <div className="text-[10px] text-[#525252] mt-1">Of those who replied</div>
            </div>
          </div>
        )}
      </Card>

      <Card className="border border-[#E5E5E5] rounded-none p-5">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
          <div>
            <h2 className="text-xs font-bold uppercase tracking-[0.15em] text-[#A3A3A3] flex items-center gap-1.5">
              <Envelope size={14} /> Booking Inquiry Email
            </h2>
            <p className="text-xs text-[#525252] mt-1 max-w-2xl leading-snug">
              Template fired by the <strong>Send Inquiry Email</strong> button on the
              Booking Check-ins page. Operators use it to nudge leads who clicked the
              meeting link but never actually scheduled a call. {PLACEHOLDER_HELP}.
            </p>
          </div>
          <Button
            onClick={saveAll}
            disabled={saving}
            size="sm"
            className="bg-[#0055FF] hover:bg-[#003ECB] text-white font-bold rounded-none h-9 px-4 text-xs uppercase tracking-wider"
            data-testid="save-inquiry-template-btn"
          >
            <FloppyDisk size={12} className="mr-1.5" /> {saving ? "Saving…" : "Save template"}
          </Button>
        </div>

        <div className="space-y-3">
          {KEYS.map(k => (
            <div key={k.key} className="flex flex-col gap-1.5">
              <label className="text-[10px] font-bold uppercase tracking-wider text-[#525252]">
                {k.label}
              </label>
              {k.type === "richtext" ? (
                <RichTextEditor
                  html={values[k.key] ?? ""}
                  onChange={html => update(k.key, html)}
                  minHeight={360}
                  placeholder="Hi {{FirstName}}, …"
                  testId={`inquiry-input-${k.key}`}
                />
              ) : k.type === "textarea" ? (
                <textarea
                  value={values[k.key] ?? ""}
                  onChange={e => update(k.key, e.target.value)}
                  rows={14}
                  className="w-full border border-[#E5E5E5] px-3 py-2 text-xs font-mono focus:outline-none focus:border-[#0055FF] leading-relaxed"
                  data-testid={`inquiry-input-${k.key}`}
                />
              ) : (
                <Input
                  value={values[k.key] ?? ""}
                  onChange={e => update(k.key, e.target.value)}
                  className="rounded-none border-[#E5E5E5] h-9 text-sm"
                  data-testid={`inquiry-input-${k.key}`}
                />
              )}
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
